import type { ProjectPermission } from './authorization.js';
import { ELEMENT_CATEGORIES, type ElementCategory } from './element.js';
import { waitingToJoinTitle } from './join-links.js';
import { TAB_SLUGS, type WorkspaceTab } from './project.js';
import type { WorkflowStatus } from './scene-validation.js';

/**
 * THE PRODUCTION COMMAND CENTER — the Overview tab (owner brief, 2026-10-29).
 *
 * One question per surface: where is the production, what is done, what is
 * missing, what needs a person, and what to do next. The answer is DERIVED here,
 * from facts the API counts on the rows themselves, by one pure function — so the
 * next action, the action list and the workflow map can never disagree with each
 * other, and the rule can be asserted without a database or a browser.
 *
 * The API calls `deriveOverview` and sends both the facts and the answer; the SPA
 * renders them and computes nothing. The same split as `GET /setup`, which is
 * the one place `nextStep` is computed.
 *
 * THREE THINGS THIS FILE REFUSES TO DO:
 *
 * - An overall percentage. Scenes, locations and the detailed breakdown are
 *   independent workflows; a production can finish its locations and never run a
 *   breakdown, and "66 % complete" would be a number about nothing.
 * - A zero that is really "not started". No location requirements because the
 *   analysis never ran is not "0 locations required", which would be a claim about
 *   the screenplay. `extractionHasRun` decides which sentence is true.
 * - A count it cannot trace. Every figure in a title is a fact the API counted;
 *   where a section could not be read the section says so, never 0.
 */

// ─── Vocabulary ────────────────────────────────────────────────────────────────

/**
 * How a workflow (or one step of it) is presented. A presentation model, not a
 * domain status: each module keeps its own state machine and is MAPPED here.
 */
export const OVERVIEW_DISPLAY_STATES = [
  'NOT_STARTED',
  'PROCESSING',
  'READY_FOR_REVIEW',
  'IN_PROGRESS',
  'COMPLETED',
  'NEEDS_ATTENTION',
  'OUTDATED',
  'FAILED',
  /** The section could not be read, or this person may not see it. */
  'UNAVAILABLE',
] as const;
export type OverviewDisplayState = (typeof OVERVIEW_DISPLAY_STATES)[number];

/** Every state is said in words; the colour only repeats it. */
export const OVERVIEW_DISPLAY_STATE_LABELS: Readonly<Record<OverviewDisplayState, string>> = Object.freeze({
  NOT_STARTED: 'Not started',
  PROCESSING: 'Processing',
  READY_FOR_REVIEW: 'Ready for review',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  NEEDS_ATTENTION: 'Needs attention',
  OUTDATED: 'Outdated',
  FAILED: 'Failed',
  UNAVAILABLE: 'Not available',
});

/** The four nodes of the workflow map. */
export type OverviewNodeId = 'SCREENPLAY' | 'SCENES' | 'LOCATIONS' | 'BREAKDOWN';

/**
 * What an action is about: a node, the AI analysis (which writes into two of
 * them), or the team — people asking to join through the invitation link.
 */
export type OverviewModule = OverviewNodeId | 'ANALYSIS' | 'TEAM';

export const OVERVIEW_MODULE_LABELS: Readonly<Record<OverviewModule, string>> = Object.freeze({
  SCREENPLAY: 'Screenplay',
  SCENES: 'Scenes',
  LOCATIONS: 'Locations',
  BREAKDOWN: 'Production elements',
  ANALYSIS: 'AI analysis',
  TEAM: 'Team',
});

/**
 * A section of the answer that may be missing for a reason the reader must be
 * told: this person may not see it (`NO_ACCESS`), or it could not be read
 * (`ERROR`). Neither is ever rendered as zero.
 */
export type OverviewSection<T> =
  | { readonly status: 'OK'; readonly value: T }
  | { readonly status: 'NO_ACCESS' }
  | { readonly status: 'ERROR' };

// ─── Facts: what the API counted ─────────────────────────────────────────────

export interface OverviewRunStage {
  readonly stage: string;
  readonly status: string;
  readonly completedUnits: number;
  readonly totalUnits: number | null;
  readonly failedUnits: number;
  readonly failureCode: string | null;
}

/** The latest pipeline run of the current version — what `GET …/progress` reports. */
export interface OverviewRun {
  readonly status: string;
  readonly failureCode: string | null;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly stages: readonly OverviewRunStage[];
}

export interface OverviewScreenplayFacts {
  readonly scriptVersionId: string;
  readonly versionNumber: number;
  /** Live drafts of the production's screenplay, the current one included. */
  readonly versionCount: number;
  readonly sourceFormat: string;
  readonly pageCount: number | null;
  readonly uploadedAt: string | null;
  readonly run: OverviewRun | null;
}

/**
 * The scene structure, as scene validation computes it (`SceneValidationService`):
 * the summary is that service's own, never re-derived from columns here.
 */
export interface OverviewSceneFacts {
  readonly detected: number;
  readonly active: number;
  readonly confirmed: number;
  readonly toReview: number;
  readonly needsAttention: number;
  readonly excluded: number;
  /** Active scenes changed after the AI read them. */
  readonly outOfDate: number;
  /**
   * Active scenes with text the AI has never read, in a screenplay it has read —
   * a scene added by hand after the analysis. Scene validation's own count.
   */
  readonly unread: number;
  /** Active scenes the AI has read at all (current or out of date). */
  readonly analysed: number;
}

/**
 * Location requirements ARE the unique filming environments: one requirement is
 * one shared location profile ("HOTEL ROOM, scenes 8, 14, 21"), reached from each
 * scene through `requirement_scene`. Counting requirements therefore never counts
 * the same place once per scene.
 */
export interface OverviewLocationFacts {
  readonly total: number;
  /** PROPOSED or DRAFT — nobody has decided whether the screenplay needs it. */
  readonly undecided: number;
  readonly approved: number;
  readonly dismissed: number;
  /** Live, not dismissed, flagged by a script revision for a second look. */
  readonly needsRecheck: number;
  /** Approved requirements with at least one search run, whatever it found. */
  readonly searched: number;
  /** Approved requirements with at least one candidate still in the running. */
  readonly withCandidates: number;
  /** Approved requirements with at least one SHORTLIST candidate. */
  readonly shortlisted: number;
  /** Approved requirements with a confirmed primary location. */
  readonly selected: number;
  /** Approved, searched, and not one candidate in the running. */
  readonly unmatched: number;
  /** The one unmatched requirement, when there is exactly one — a deep link. */
  readonly unmatchedRequirementId: string | null;
  /** Approved requirements with candidates and none shortlisted yet. */
  readonly candidatesToShortlist: number;
  /** Approved requirements with a shortlist and no confirmed primary. */
  readonly shortlistsToDecide: number;
  /** Scenes of the CURRENT draft that no requirement covers — what the review screen shows. */
  readonly scenesWithoutRequirements: number;
}

export interface OverviewCategoryFacts {
  readonly category: ElementCategory;
  /** Elements on the sheet in this category, one per scene they are on. */
  readonly confirmed: number;
  /** AI proposals nobody has decided, in scenes whose breakdown is not signed off. */
  readonly toReview: number;
  readonly rejected: number;
}

export interface OverviewBreakdownFacts {
  /** AI proposals on the current draft's active scenes, any decision. */
  readonly proposals: number;
  readonly toReview: number;
  /** Element-on-scene links on the sheet — what the production agreed. */
  readonly confirmedTags: number;
  /** Distinct production elements those links name. */
  readonly uniqueElements: number;
  readonly scenesSignedOff: number;
  /** Signed off once, then reopened by an edit or by hand. */
  readonly scenesReopened: number;
  /** Only categories with something in them, in the sheet's own order. */
  readonly categories: readonly OverviewCategoryFacts[];
}

export interface OverviewDependencyFacts {
  /** Open impacts of a script comparison, by severity. A BLOCKER pauses matching. */
  readonly openImpacts: { readonly blocker: number; readonly warning: number; readonly info: number };
  /** Location profiles signed off, then reopened because a scene they rest on changed. */
  readonly locationProfilesReopened: number;
  /** Live requirements read from an EARLIER draft than the current one. */
  readonly requirementsFromEarlierDraft: number;
}

export interface OverviewSetupFacts {
  readonly nextStep: 'start' | 'scenes' | 'choose' | null;
  readonly setupCompletedAt: string | null;
  readonly scenesConfirmedAt: string | null;
  readonly workflows: { readonly locations: WorkflowStatus; readonly breakdown: WorkflowStatus };
}

/**
 * The team's own waiting work, as the production's OWNER sees it: people who asked
 * to join through the invitation link (owner brief 2026-11-02, §8). Only the owner
 * decides a request, so for everyone else this section is `NO_ACCESS` and nothing
 * about requests reaches their Overview.
 */
export interface OverviewTeamFacts {
  /** Requests waiting for the owner's decision. */
  readonly pendingJoinRequests: number;
}

/** What THIS person may do here — the hint, never the gate (the server still checks). */
export interface OverviewAbilities {
  readonly uploadScript: boolean;
  readonly reviewScript: boolean;
  readonly runAi: boolean;
  readonly approveRequirements: boolean;
  readonly searchLocations: boolean;
  readonly editCandidates: boolean;
  readonly confirmSelection: boolean;
}

export interface OverviewFacts {
  /** The paid period lapsed: everything reads, nothing changes. */
  readonly readOnly: boolean;
  readonly setup: OverviewSetupFacts;
  /** `value: null` — no screenplay yet. */
  readonly screenplay: OverviewSection<OverviewScreenplayFacts | null>;
  /** `value: null` — no screenplay, so no scenes to speak of. */
  readonly scenes: OverviewSection<OverviewSceneFacts | null>;
  readonly locations: OverviewSection<OverviewLocationFacts>;
  readonly breakdown: OverviewSection<OverviewBreakdownFacts>;
  readonly dependencies: OverviewSection<OverviewDependencyFacts>;
  /**
   * Optional: an answer from a build without it, or a caller that is not the
   * owner, simply has no team line. Never read as zero.
   */
  readonly team?: OverviewSection<OverviewTeamFacts>;
  readonly can: OverviewAbilities;
}

// ─── The answer ────────────────────────────────────────────────────────────────

/** The guided-flow pages (`/projects/:address/<step>`), as scene validation defines them. */
export type OverviewFlowStep = 'start' | 'scenes' | 'choose' | 'breakdown' | 'explore' | 'elements';

/**
 * Where a control goes, as data. The SPA turns it into a path with the
 * production's own address (`overviewTargetPath`), so the API never needs to
 * know the slug and a target is assertable without a router.
 */
export type OverviewTarget =
  | {
      readonly kind: 'FLOW';
      readonly step: OverviewFlowStep;
      readonly id?: string;
      readonly query?: Readonly<Record<string, string>>;
    }
  | {
      readonly kind: 'TAB';
      readonly tab: WorkspaceTab;
      readonly sub?: string;
      readonly query?: Readonly<Record<string, string>>;
    };

export interface OverviewCta {
  readonly label: string;
  readonly target: OverviewTarget;
  /** False when this person may not do it, or the production is read-only: shown as a way to LOOK. */
  readonly executable: boolean;
  /** The permission it takes — the SPA names it when `executable` is false. */
  readonly requires: ProjectPermission | null;
}

export type OverviewActionCategory = 'BLOCKING' | 'REVIEW' | 'RECOMMENDED';

export const OVERVIEW_ACTION_CATEGORY_LABELS: Readonly<Record<OverviewActionCategory, string>> = Object.freeze({
  BLOCKING: 'Blocking',
  REVIEW: 'Needs review',
  RECOMMENDED: 'Recommended',
});

/**
 * One line of work. AGGREGATED: eight props waiting are one action that says 8,
 * not eight cards; the screen it opens lists them.
 */
export interface OverviewAction {
  /** Stable, so the SPA can key and a test can name it. */
  readonly id: string;
  readonly category: OverviewActionCategory;
  /** Higher first, within its category. Severity and reach, never age. */
  readonly priority: number;
  readonly module: OverviewModule;
  readonly title: string;
  readonly description: string;
  readonly affectedCount: number | null;
  readonly cta: OverviewCta;
}

export type OverviewNextActionKind =
  /** One thing to do. */
  | 'ACT'
  /** Two equally valid ways on — neither is a prerequisite of the other. */
  | 'CHOOSE'
  /** Work is running; the honest action is to wait for it. */
  | 'WAIT'
  /** Nothing is waiting on a person. NOT "the production is ready". */
  | 'UP_TO_DATE'
  /** The facts this needs could not be read. */
  | 'UNAVAILABLE';

export interface OverviewNextAction {
  readonly kind: OverviewNextActionKind;
  readonly module: OverviewModule | null;
  readonly title: string;
  readonly description: string;
  readonly ctas: readonly OverviewCta[];
}

export interface OverviewStep {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly state: OverviewDisplayState;
}

export interface OverviewNode {
  readonly id: OverviewNodeId;
  readonly state: OverviewDisplayState;
  readonly headline: string;
  readonly detail: string | null;
  /** Null while the node cannot be opened yet (it says why in `detail`). */
  readonly target: OverviewTarget | null;
  readonly attention: number;
  readonly steps: readonly OverviewStep[];
}

export interface OverviewDerived {
  readonly nextAction: OverviewNextAction;
  readonly actions: readonly OverviewAction[];
  readonly nodes: Readonly<Record<OverviewNodeId, OverviewNode>>;
}

export interface ProjectOverview extends OverviewDerived {
  readonly facts: OverviewFacts;
}

// ─── Small pure rules ──────────────────────────────────────────────────────────

/** A run that will not change any more. */
export function runFinished(status: string): boolean {
  return status === 'COMPLETED' || status === 'PARTIAL' || status === 'FAILED';
}

/**
 * Whether the AI analysis was ever attempted on this draft — the same rule as the
 * SPA's `extractionHasRun` (`components/AnalyseWithAi.tsx`): an EXTRACT stage, or
 * a FAILED run with no PARSE stage behind it. A failed PARSE is the screenplay
 * failing to be read, which no model was ever asked about.
 */
export function extractionHasRun(run: OverviewRun | null): boolean {
  if (run === null) return false;
  if (run.stages.some((s) => s.stage === 'EXTRACT')) return true;

  return run.status === 'FAILED' && !run.stages.some((s) => s.stage === 'PARSE');
}

/**
 * Where reading the screenplay stands. `UNKNOWN` when the scenes could not be
 * read: with no scene count, a finished parse cannot be told from a failed one,
 * and "your screenplay could not be read" would be a guess.
 */
export type ParseState = 'NONE' | 'PROCESSING' | 'FAILED' | 'READ' | 'UNKNOWN';

export function parseState(screenplay: OverviewScreenplayFacts | null, scenes: OverviewSceneFacts | null): ParseState {
  if (screenplay === null) return 'NONE';

  const anyScene = scenes !== null && scenes.active + scenes.excluded > 0;
  const run = screenplay.run;
  const isParse = run?.stages.some((s) => s.stage === 'PARSE') ?? false;

  if (anyScene) return 'READ';
  if (run === null) return 'PROCESSING';
  if (isParse && run.status === 'FAILED') return 'FAILED';
  if (!runFinished(run.status)) return 'PROCESSING';

  // Finished, and not one scene came out of it: since 2026-10-27 a parse that
  // recognises no heading FAILS (`NO_SCENES_FOUND`), so this is that, or older.
  return 'FAILED';
}

/** Where the AI analysis of the current draft stands. */
export type ExtractionState = 'NOT_RUN' | 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'PARTIAL' | 'FAILED';

export function extractionState(run: OverviewRun | null): ExtractionState {
  if (run === null) return 'NOT_RUN';

  const extract = run.stages.find((s) => s.stage === 'EXTRACT');
  const isParse = run.stages.some((s) => s.stage === 'PARSE');

  if (extract === undefined) {
    // The parse run is a different job; an extraction run with no stage yet is
    // one the worker has not claimed (QUEUED), or one that died before it did.
    if (isParse) return 'NOT_RUN';
    if (run.status === 'FAILED') return 'FAILED';
    if (!runFinished(run.status)) return 'QUEUED';

    return 'NOT_RUN';
  }

  if (run.status === 'FAILED') return 'FAILED';
  if (run.status === 'PARTIAL') return 'PARTIAL';
  if (run.status === 'COMPLETED') return 'COMPLETED';

  return 'RUNNING';
}

/**
 * The path a target opens, with the production's own address.
 *
 * `FLOW` pages are the guided flow's (`/scenes?filter=…`, `/choose`, `/breakdown`,
 * `/explore/:requirementId`, `/elements`); `TAB` pages are the workspace's. The
 * query string is built here, sorted, so one target is always one URL.
 */
export function overviewTargetPath(address: string, target: OverviewTarget): string {
  const query = target.query === undefined ? '' : queryString(target.query);

  if (target.kind === 'FLOW') {
    return `/projects/${address}/${target.step}${target.id === undefined ? '' : `/${target.id}`}${query}`;
  }

  return `/projects/${address}/${TAB_SLUGS[target.tab]}${target.sub === undefined ? '' : `/${target.sub}`}${query}`;
}

function queryString(query: Readonly<Record<string, string>>): string {
  const keys = Object.keys(query).sort();
  if (keys.length === 0) return '';

  return `?${keys.map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(query[k] ?? '')}`).join('&')}`;
}

/**
 * The stored format, as a producer would name the file. `OFFICE` is every word
 * processor format the validator accepts (.docx, .doc, .odt, Fade In), and
 * `FOUNTAIN` is plain text, pasted text included.
 */
const SCREENPLAY_FORMAT_LABELS: Readonly<Record<string, string>> = Object.freeze({
  PDF: 'PDF',
  FDX: 'Final Draft',
  FOUNTAIN: 'Plain text',
  OFFICE: 'Document',
});

export function screenplayFormatLabel(format: string): string {
  return SCREENPLAY_FORMAT_LABELS[format] ?? format;
}

function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

function sceneWord(n: number): string {
  return plural(n, 'scene', 'scenes');
}

// ─── The derivation ────────────────────────────────────────────────────────────

const SCRIPT_TAB: OverviewTarget = { kind: 'TAB', tab: 'SCRIPT_AND_SCENES', sub: 'script' };
const LOCATIONS_TAB: OverviewTarget = { kind: 'TAB', tab: 'LOCATIONS' };

/** Priorities. Higher first; the bands keep the categories in order whatever the mix. */
const P = {
  UPLOAD: 990,
  PARSE_FAILED: 980,
  NO_ACTIVE_SCENES: 970,
  SCENES_NEED_ATTENTION_GATE: 950,
  SCENES_CHANGED: 930,
  ANALYSIS_FAILED: 910,
  SCENES_TO_CONFIRM: 790,
  SCENES_UNREAD: 720,
  FINISH_SCENE_VALIDATION: 780,
  REQUIREMENTS_TO_DECIDE: 700,
  REQUIREMENTS_TO_RECHECK: 690,
  ELEMENTS_TO_REVIEW: 680,
  BREAKDOWNS_REOPENED: 670,
  SCENES_NEED_ATTENTION: 640,
  SCENES_NOT_ANALYSED: 600,
  SCENES_WITHOUT_REQUIREMENT: 560,
  NOTHING_PROPOSED: 550,
  SCENES_LEFT_UNCONFIRMED: 450,
  UNMATCHED: 320,
  UNSEARCHED: 300,
  TO_SHORTLIST: 250,
  TO_SELECT: 240,
  START_WORKFLOW: 200,
  SEARCH_WITHOUT_SCRIPT: 100,
  /** Below every review of the production's own work; still a review. */
  JOIN_REQUESTS: 400,
} as const;

/**
 * The Overview's answer, from the facts alone.
 *
 * Deterministic and total: every combination of facts yields one next action, a
 * list of actions in a stable order, and one node per workflow. Nothing here
 * reads a clock, a cache or a browser.
 */
export function deriveOverview(facts: OverviewFacts): OverviewDerived {
  const ctx = context(facts);
  const actions = sortActions([...collectActions(ctx), ...teamActions(facts)]);
  const nodes = {
    SCREENPLAY: screenplayNode(ctx),
    SCENES: scenesNode(ctx),
    LOCATIONS: locationsNode(ctx),
    BREAKDOWN: breakdownNode(ctx),
  };

  return { nextAction: nextAction(ctx, actions), actions, nodes };
}

interface Ctx {
  readonly facts: OverviewFacts;
  readonly screenplay: OverviewScreenplayFacts | null;
  readonly scenes: OverviewSceneFacts | null;
  readonly locations: OverviewLocationFacts | null;
  readonly breakdown: OverviewBreakdownFacts | null;
  readonly deps: OverviewDependencyFacts | null;
  readonly parse: ParseState;
  readonly extraction: ExtractionState;
  readonly extractionRan: boolean;
  /**
   * The workflow pages (`/choose`, `/breakdown`, `/explore`, `/elements`) open
   * once the scenes are confirmed — `nextStep` 'choose' — or setup is finished.
   * Before that they send the producer back to `/scenes`, so nothing here may
   * point at them.
   */
  readonly workflowsOpen: boolean;
  readonly cta: (label: string, target: OverviewTarget, requires: ProjectPermission | null) => OverviewCta;
}

function context(facts: OverviewFacts): Ctx {
  const screenplay = facts.screenplay.status === 'OK' ? facts.screenplay.value : null;
  const scenes = facts.scenes.status === 'OK' ? facts.scenes.value : null;
  const run = screenplay?.run ?? null;
  const can = facts.can;

  const allowed: Readonly<Partial<Record<ProjectPermission, boolean>>> = {
    SCRIPT_UPLOAD: can.uploadScript,
    SCRIPT_REVIEW: can.reviewScript,
    AI_RUN: can.runAi,
    REQUIREMENT_APPROVE: can.approveRequirements,
    SOURCING_ACTIVATE: can.searchLocations,
    CANDIDATE_EDIT: can.editCandidates,
    SELECTION_CONFIRM: can.confirmSelection,
  };

  return {
    facts,
    screenplay,
    scenes,
    locations: facts.locations.status === 'OK' ? facts.locations.value : null,
    breakdown: facts.breakdown.status === 'OK' ? facts.breakdown.value : null,
    deps: facts.dependencies.status === 'OK' ? facts.dependencies.value : null,
    parse:
      screenplay !== null && facts.scenes.status !== 'OK' ? 'UNKNOWN' : parseState(screenplay, scenes),
    extraction: extractionState(run),
    extractionRan: extractionHasRun(run),
    workflowsOpen: screenplay !== null && (facts.setup.nextStep === 'choose' || facts.setup.nextStep === null),
    cta: (label, target, requires) => ({
      label,
      target,
      requires,
      executable: requires === null || (!facts.readOnly && allowed[requires] === true),
    }),
  };
}

/** Upload goes to step 1 while the guided path is open; after it, to the Script tab. */
function uploadTarget(ctx: Ctx): OverviewTarget {
  return ctx.facts.setup.setupCompletedAt === null ? { kind: 'FLOW', step: 'start' } : SCRIPT_TAB;
}

function scenesTarget(filter?: 'TO_REVIEW' | 'NEEDS_ATTENTION' | 'EXCLUDED'): OverviewTarget {
  return filter === undefined ? { kind: 'FLOW', step: 'scenes' } : { kind: 'FLOW', step: 'scenes', query: { filter } };
}

/** The requirements review: the flow page when it is open, the Script tab's otherwise. */
function requirementsTarget(ctx: Ctx): OverviewTarget {
  return ctx.workflowsOpen ? { kind: 'FLOW', step: 'breakdown' } : { kind: 'TAB', tab: 'SCRIPT_AND_SCENES', sub: 'review' };
}

/** The detailed breakdown: `/elements` when open, the Script tab's breakdown cards otherwise. */
function elementsTarget(ctx: Ctx): OverviewTarget {
  return ctx.workflowsOpen ? { kind: 'FLOW', step: 'elements' } : { kind: 'TAB', tab: 'SCRIPT_AND_SCENES', sub: 'breakdown' };
}

function exploreTarget(ctx: Ctx, requirementId: string | null): OverviewTarget {
  if (!ctx.workflowsOpen) return LOCATIONS_TAB;

  return requirementId === null ? { kind: 'FLOW', step: 'explore' } : { kind: 'FLOW', step: 'explore', id: requirementId };
}

function hasBreakdownWork(b: OverviewBreakdownFacts): boolean {
  return b.proposals > 0 || b.confirmedTags > 0 || b.scenesSignedOff > 0 || b.scenesReopened > 0;
}

function analysisRunning(ctx: Ctx): boolean {
  return ctx.extraction === 'QUEUED' || ctx.extraction === 'RUNNING';
}

function failedUnits(ctx: Ctx): { count: number; didNotFit: boolean } {
  const stage = ctx.screenplay?.run?.stages.find((s) => s.stage === 'EXTRACT');
  if (stage === undefined) return { count: 0, didNotFit: false };

  return { count: stage.failedUnits, didNotFit: stage.failureCode === 'SCENES_DID_NOT_FIT' };
}

// ─── Actions ───────────────────────────────────────────────────────────────────

function collectActions(ctx: Ctx): OverviewAction[] {
  const actions: OverviewAction[] = [];
  const add = (action: OverviewAction) => actions.push(action);
  const { screenplay, scenes, locations: L, breakdown: B, deps } = ctx;

  if (ctx.facts.screenplay.status === 'OK' && ctx.parse === 'NONE') {
    add({
      id: 'screenplay.upload',
      category: 'BLOCKING',
      priority: P.UPLOAD,
      module: 'SCREENPLAY',
      title: 'Upload your screenplay',
      description: 'Scenes, location requirements and the breakdown are all read from it.',
      affectedCount: null,
      cta: ctx.cta('Upload screenplay', uploadTarget(ctx), 'SCRIPT_UPLOAD'),
    });
    add({
      id: 'locations.search-without-script',
      category: 'RECOMMENDED',
      priority: P.SEARCH_WITHOUT_SCRIPT,
      module: 'LOCATIONS',
      title: 'Search locations without a screenplay',
      description: 'Browse the location catalogue now; requirements can be added later.',
      affectedCount: null,
      cta: ctx.cta('Open locations', LOCATIONS_TAB, null),
    });

    return actions;
  }

  if (screenplay !== null && ctx.parse === 'FAILED') {
    const code = screenplay.run?.failureCode ?? null;
    add({
      id: 'screenplay.parse-failed',
      category: 'BLOCKING',
      priority: P.PARSE_FAILED,
      module: 'SCREENPLAY',
      title: 'Your screenplay could not be read',
      description:
        code === 'NO_SCENES_FOUND' || code === null
          ? `No scene headings (INT. or EXT.) were recognised in draft v${String(screenplay.versionNumber)}. Upload it in a standard screenplay format, or paste it as text.`
          : `Reading draft v${String(screenplay.versionNumber)} did not finish. Upload it again; nothing else in the production changes.`,
      affectedCount: null,
      cta: ctx.cta('Upload again', uploadTarget(ctx), 'SCRIPT_UPLOAD'),
    });

    return actions;
  }

  if (ctx.parse !== 'READ' || scenes === null) return actions;

  // ── Scenes ──
  if (scenes.active === 0) {
    add({
      id: 'scenes.none-active',
      category: 'BLOCKING',
      priority: P.NO_ACTIVE_SCENES,
      module: 'SCENES',
      title: 'Every scene is excluded',
      description: `All ${sceneWord(scenes.excluded)} were excluded, so there is nothing to analyse. Restore the ones that are scenes.`,
      affectedCount: scenes.excluded,
      cta: ctx.cta('Review excluded scenes', scenesTarget('EXCLUDED'), 'SCRIPT_REVIEW'),
    });
  }

  if (scenes.needsAttention > 0) {
    const gate = !ctx.workflowsOpen;
    add({
      id: 'scenes.needs-attention',
      category: gate ? 'BLOCKING' : 'REVIEW',
      priority: gate ? P.SCENES_NEED_ATTENTION_GATE : P.SCENES_NEED_ATTENTION,
      module: 'SCENES',
      title: `Fix ${plural(scenes.needsAttention, 'scene that needs', 'scenes that need')} attention`,
      description:
        'ScoutAround flagged their structure: no text, a heading it could not read, a line that looks like a missed heading, or a repeated scene number.' +
        (gate ? ' The scene list cannot be confirmed until each one is fixed, confirmed or excluded.' : ''),
      affectedCount: scenes.needsAttention,
      cta: ctx.cta('Fix scenes', scenesTarget('NEEDS_ATTENTION'), 'SCRIPT_REVIEW'),
    });
  }

  if (scenes.toReview > 0) {
    if (!ctx.workflowsOpen) {
      add({
        id: 'scenes.to-confirm',
        category: 'REVIEW',
        priority: P.SCENES_TO_CONFIRM,
        module: 'SCENES',
        title: 'Review extracted scenes',
        description: `ScoutAround identified ${sceneWord(scenes.active)} in your screenplay${scenes.confirmed > 0 ? `; ${String(scenes.confirmed)} are confirmed` : ''}. Confirm that each heading and boundary is right before the production analysis reads them.`,
        affectedCount: scenes.toReview,
        cta: ctx.cta('Review scenes', scenesTarget('TO_REVIEW'), 'SCRIPT_REVIEW'),
      });
    } else {
      // A production past the scene step whose scenes were never confirmed one
      // by one — it passed the old scene overview. Not inferred as confirmed.
      add({
        id: 'scenes.left-unconfirmed',
        category: 'REVIEW',
        priority: P.SCENES_LEFT_UNCONFIRMED,
        module: 'SCENES',
        title: `Confirm ${plural(scenes.toReview, 'remaining scene', 'remaining scenes')}`,
        description: `${sceneWord(scenes.toReview)} of ${String(scenes.active)} ${scenes.toReview === 1 ? 'has' : 'have'} not been confirmed. Both workflows read the scene list, so a wrong boundary reaches each of them.`,
        affectedCount: scenes.toReview,
        cta: ctx.cta('Review scenes', scenesTarget('TO_REVIEW'), 'SCRIPT_REVIEW'),
      });
    }
  } else if (ctx.facts.setup.nextStep === 'scenes' && scenes.needsAttention === 0 && scenes.active > 0) {
    add({
      id: 'scenes.finish-validation',
      category: 'REVIEW',
      priority: P.FINISH_SCENE_VALIDATION,
      module: 'SCENES',
      title: 'Finish scene validation',
      description: `All ${sceneWord(scenes.active)} are confirmed. Confirm the scene list to open the location analysis and the detailed breakdown.`,
      affectedCount: null,
      cta: ctx.cta('Confirm scene list', scenesTarget(), 'SCRIPT_REVIEW'),
    });
  }

  // A scene added after the analysis has no proposals at all; /choose reads it
  // together with the changed ones, in one press.
  const unread = scenes.unread > 0 ? `; ${sceneWord(scenes.unread)} added since ${scenes.unread === 1 ? 'has' : 'have'} not been read at all` : '';

  if (scenes.outOfDate > 0 && ctx.workflowsOpen) {
    const reach: string[] = [];
    if (deps !== null && deps.locationProfilesReopened > 0) {
      reach.push(plural(deps.locationProfilesReopened, 'location profile', 'location profiles'));
    }
    if (B !== null && B.scenesReopened > 0) {
      reach.push(plural(B.scenesReopened, 'scene breakdown', 'scene breakdowns'));
    }

    add({
      id: 'dependencies.scenes-changed',
      category: 'BLOCKING',
      priority: P.SCENES_CHANGED,
      module: 'SCENES',
      title: 'Review changes affecting your production',
      description:
        `${sceneWord(scenes.outOfDate)} changed after the AI read ${scenes.outOfDate === 1 ? 'it' : 'them'}. ` +
        'The earlier proposals are kept, but they describe the scene as it was' +
        (reach.length > 0 ? `; ${reach.join(' and ')} reopened for review` : '') +
        `${unread}.`,
      affectedCount: scenes.outOfDate + scenes.unread,
      cta: ctx.cta('Review changes', { kind: 'FLOW', step: 'choose' }, 'AI_RUN'),
    });
  } else if (scenes.unread > 0 && ctx.workflowsOpen) {
    add({
      id: 'analysis.scenes-unread',
      category: 'REVIEW',
      priority: P.SCENES_UNREAD,
      module: 'ANALYSIS',
      title: `${sceneWord(scenes.unread)} added after the AI analysis ${scenes.unread === 1 ? 'has' : 'have'} not been read`,
      description:
        'Nothing was suggested for them, so neither workflow covers them yet. Read them from the workflow choice; nothing runs until you press it there.',
      affectedCount: scenes.unread,
      cta: ctx.cta('Read new scenes', { kind: 'FLOW', step: 'choose' }, 'AI_RUN'),
    });
  }

  if (!ctx.workflowsOpen) return actions;

  // ── The AI analysis ──
  if (ctx.extraction === 'FAILED') {
    add({
      id: 'analysis.failed',
      category: 'BLOCKING',
      priority: P.ANALYSIS_FAILED,
      module: 'ANALYSIS',
      title: 'The AI analysis did not finish',
      description:
        screenplay?.run?.failureCode === 'EXTRACT_INTERRUPTED'
          ? 'It was interrupted before it finished. Run it again; nothing was lost.'
          : 'The last analysis of this draft failed. Anything it found earlier is kept. Run it again from either workflow — one analysis covers both.',
      affectedCount: null,
      cta: ctx.cta('Run the analysis again', requirementsTarget(ctx), 'AI_RUN'),
    });
  } else if (ctx.extraction === 'PARTIAL') {
    const { count, didNotFit } = failedUnits(ctx);
    if (count > 0) {
      add({
        id: 'analysis.partial',
        category: 'REVIEW',
        priority: P.SCENES_NOT_ANALYSED,
        module: 'ANALYSIS',
        title: `${sceneWord(count)} ${count === 1 ? 'was' : 'were'} not analysed`,
        description: didNotFit
          ? 'The screenplay was longer than one analysis can read. Pick the scenes that were left out and analyse them on their own.'
          : 'The AI could not read part of the screenplay. Analyse those scenes again, or break them down by hand.',
        affectedCount: count,
        cta: ctx.cta('Analyse the rest', requirementsTarget(ctx), 'AI_RUN'),
      });
    }
  }

  // ── Locations ──
  if (L !== null) {
    if (L.undecided > 0) {
      add({
        id: 'locations.requirements-to-decide',
        category: 'REVIEW',
        priority: P.REQUIREMENTS_TO_DECIDE,
        module: 'LOCATIONS',
        title: `${plural(L.undecided, 'location requirement awaits', 'location requirements await')} your decision`,
        description:
          'Approve the filming locations the screenplay needs and mark the rest not needed. Only approved requirements are searched for.',
        affectedCount: L.undecided,
        cta: ctx.cta('Review requirements', requirementsTarget(ctx), 'REQUIREMENT_APPROVE'),
      });
    }

    if (L.needsRecheck > 0) {
      add({
        id: 'locations.requirements-to-recheck',
        category: 'REVIEW',
        priority: P.REQUIREMENTS_TO_RECHECK,
        module: 'LOCATIONS',
        title: `Re-check ${plural(L.needsRecheck, 'location requirement', 'location requirements')}`,
        description: 'A script revision reached them. Confirm they still describe what the screenplay needs.',
        affectedCount: L.needsRecheck,
        cta: ctx.cta('Re-check requirements', requirementsTarget(ctx), 'REQUIREMENT_APPROVE'),
      });
    }

    if (L.scenesWithoutRequirements > 0 && L.total > 0 && ctx.extractionRan) {
      add({
        id: 'locations.scenes-without-requirement',
        category: 'REVIEW',
        priority: P.SCENES_WITHOUT_REQUIREMENT,
        module: 'LOCATIONS',
        title: `${sceneWord(L.scenesWithoutRequirements)} ${L.scenesWithoutRequirements === 1 ? 'has' : 'have'} no location requirement`,
        description:
          'The analysis found nothing for them, often because the place has no matching type in the catalogue. Nobody searches for them until a requirement is added.',
        affectedCount: L.scenesWithoutRequirements,
        cta: ctx.cta('Add requirements', requirementsTarget(ctx), 'REQUIREMENT_APPROVE'),
      });
    }

    if (L.unmatched > 0) {
      add({
        id: 'locations.unmatched',
        category: 'RECOMMENDED',
        priority: P.UNMATCHED,
        module: 'LOCATIONS',
        title: `Find locations for ${plural(L.unmatched, 'unmatched requirement', 'unmatched requirements')}`,
        description:
          'A search ran and found no candidate. Widen the search area, loosen a must-have, or add a place by hand.',
        affectedCount: L.unmatched,
        cta: ctx.cta('Find locations', exploreTarget(ctx, L.unmatched === 1 ? L.unmatchedRequirementId : null), 'SOURCING_ACTIVATE'),
      });
    }

    const unsearched = L.approved - L.searched;
    if (unsearched > 0) {
      add({
        id: 'locations.unsearched',
        category: 'RECOMMENDED',
        priority: P.UNSEARCHED,
        module: 'LOCATIONS',
        title: `Search for ${plural(unsearched, 'approved location', 'approved locations')}`,
        description: 'They are approved, and no search has run for them yet.',
        affectedCount: unsearched,
        cta: ctx.cta('Start searching', exploreTarget(ctx, null), 'SOURCING_ACTIVATE'),
      });
    }

    if (L.candidatesToShortlist > 0) {
      add({
        id: 'locations.to-shortlist',
        category: 'RECOMMENDED',
        priority: P.TO_SHORTLIST,
        module: 'LOCATIONS',
        title: `Shortlist candidates for ${plural(L.candidatesToShortlist, 'requirement', 'requirements')}`,
        description: 'Candidates were found and none is shortlisted yet.',
        affectedCount: L.candidatesToShortlist,
        cta: ctx.cta('Open locations', LOCATIONS_TAB, 'CANDIDATE_EDIT'),
      });
    }

    if (L.shortlistsToDecide > 0) {
      add({
        id: 'locations.to-select',
        category: 'RECOMMENDED',
        priority: P.TO_SELECT,
        module: 'LOCATIONS',
        title: `Confirm a location for ${plural(L.shortlistsToDecide, 'requirement', 'requirements')}`,
        description: 'Each has a shortlist and no primary location confirmed.',
        affectedCount: L.shortlistsToDecide,
        cta: ctx.cta('Compare and confirm', LOCATIONS_TAB, 'SELECTION_CONFIRM'),
      });
    }
  }

  // ── Production elements ──
  if (B !== null) {
    if (B.toReview > 0) {
      const top = [...B.categories]
        .filter((c) => c.toReview > 0)
        .sort((a, b) => b.toReview - a.toReview);
      const named = top
        .slice(0, 3)
        .map((c) => `${ELEMENT_CATEGORIES[c.category].label} ${String(c.toReview)}`)
        .join(', ');
      const more = top.length > 3 ? ` and ${plural(top.length - 3, 'more category', 'more categories')}` : '';

      add({
        id: 'breakdown.elements-to-review',
        category: 'REVIEW',
        priority: P.ELEMENTS_TO_REVIEW,
        module: 'BREAKDOWN',
        title: `${plural(B.toReview, 'production element needs', 'production elements need')} review`,
        description: `${named}${more}. Confirm, correct or reject what the AI suggested; nothing counts as confirmed until a person says so.`,
        affectedCount: B.toReview,
        cta: ctx.cta('Review elements', elementsTarget(ctx), 'SCRIPT_REVIEW'),
      });
    }

    if (B.scenesReopened > 0) {
      add({
        id: 'breakdown.scenes-reopened',
        category: 'REVIEW',
        priority: P.BREAKDOWNS_REOPENED,
        module: 'BREAKDOWN',
        title: `Re-review ${plural(B.scenesReopened, 'scene breakdown', 'scene breakdowns')}`,
        description: 'They were signed off, then reopened because the scene or its elements changed.',
        affectedCount: B.scenesReopened,
        cta: ctx.cta('Review breakdowns', elementsTarget(ctx), 'SCRIPT_REVIEW'),
      });
    }
  }

  // ── The analysis ran and suggested nothing: a fact about THIS run, with a remedy ──
  const settled = ctx.extractionRan && !analysisRunning(ctx) && ctx.extraction !== 'FAILED';

  if (settled && L !== null && L.total === 0) {
    add({
      id: 'locations.none-proposed',
      category: 'REVIEW',
      priority: P.NOTHING_PROPOSED,
      module: 'LOCATIONS',
      title: 'The analysis suggested no location requirements',
      description: 'Add the places the screenplay needs by hand. Nothing is searched for until a requirement exists.',
      affectedCount: null,
      cta: ctx.cta('Add requirements', requirementsTarget(ctx), 'REQUIREMENT_APPROVE'),
    });
  }

  if (settled && B !== null && !hasBreakdownWork(B)) {
    add({
      id: 'breakdown.none-proposed',
      category: 'REVIEW',
      priority: P.NOTHING_PROPOSED,
      module: 'BREAKDOWN',
      title: 'The analysis suggested no production elements',
      description: 'Add cast, props, costumes and the rest by hand, scene by scene.',
      affectedCount: null,
      cta: ctx.cta('Open the breakdown', elementsTarget(ctx), 'SCRIPT_REVIEW'),
    });
  }

  // ── Starting a workflow: only when no analysis has been asked for ──
  const { locations: locWf, breakdown: bdWf } = ctx.facts.setup.workflows;
  const locationsPending = L !== null && L.total > 0;
  const elementsPending = B !== null && hasBreakdownWork(B);

  if (locWf === 'NOT_STARTED' && !locationsPending && L !== null && !ctx.extractionRan && !analysisRunning(ctx)) {
    add({
      id: 'locations.start',
      category: 'RECOMMENDED',
      priority: P.START_WORKFLOW,
      module: 'LOCATIONS',
      title: 'Start the location analysis',
      description: 'Find the filming locations the screenplay needs and search the catalogue for them.',
      affectedCount: null,
      cta: ctx.cta('Find filming locations', { kind: 'FLOW', step: 'breakdown' }, 'REQUIREMENT_APPROVE'),
    });
  }

  if (bdWf === 'NOT_STARTED' && !elementsPending && B !== null && !ctx.extractionRan && !analysisRunning(ctx)) {
    add({
      id: 'breakdown.start',
      category: 'RECOMMENDED',
      priority: P.START_WORKFLOW,
      module: 'BREAKDOWN',
      title: 'Start the detailed breakdown',
      description: 'Cast, props, costumes, vehicles and the rest, scene by scene.',
      affectedCount: null,
      cta: ctx.cta('Create detailed breakdown', { kind: 'FLOW', step: 'elements' }, 'SCRIPT_REVIEW'),
    });
  }

  return actions;
}

/**
 * PEOPLE WAITING TO JOIN — one aggregated line, for the owner alone (brief §8).
 *
 * Outside `collectActions` on purpose: that function returns early while there is
 * no screenplay, and a request to join is just as real on a production that has
 * none. A REVIEW item, but never the next best action (`nextAction` skips the
 * TEAM module): the Overview's headline is the production's own work, and this
 * line is a compact entry in the Action Center, not a card of its own.
 */
function teamActions(facts: OverviewFacts): OverviewAction[] {
  const team = facts.team;
  if (team?.status !== 'OK' || team.value.pendingJoinRequests <= 0) return [];

  const n = team.value.pendingJoinRequests;
  return [
    {
      id: 'team.join-requests',
      category: 'REVIEW',
      priority: P.JOIN_REQUESTS,
      module: 'TEAM',
      title: waitingToJoinTitle(n),
      description:
        n === 1
          ? 'They asked through your invitation link. Approve them with a role, or reject the request.'
          : 'They asked through your invitation link. Approve each one with a role, or reject the request.',
      affectedCount: n,
      cta: {
        label: 'Review requests',
        target: { kind: 'TAB', tab: 'TEAM', query: { list: 'requests', view: 'invitations' } },
        // Not a grid permission: the owner decides, and the server checks it.
        // Not gated on read-only either — a lapsed production's owner is told
        // by the approve door itself, with the plan to renew.
        executable: true,
        requires: null,
      },
    },
  ];
}

const CATEGORY_ORDER: Readonly<Record<OverviewActionCategory, number>> = { BLOCKING: 0, REVIEW: 1, RECOMMENDED: 2 };

/** Blocking, then review, then recommended; within each, priority; then id, so ties are stable. */
export function sortActions(actions: readonly OverviewAction[]): OverviewAction[] {
  return [...actions].sort(
    (a, b) =>
      CATEGORY_ORDER[a.category] - CATEGORY_ORDER[b.category] ||
      b.priority - a.priority ||
      a.id.localeCompare(b.id),
  );
}

// ─── The next best action ──────────────────────────────────────────────────────

function fromAction(action: OverviewAction): OverviewNextAction {
  return {
    kind: 'ACT',
    module: action.module,
    title: action.title,
    description: action.description,
    ctas: [action.cta],
  };
}

function nextAction(ctx: Ctx, everything: readonly OverviewAction[]): OverviewNextAction {
  const { facts, screenplay, scenes, locations: L, breakdown: B } = ctx;
  // The team's line is an Action Center entry, never the headline (see teamActions).
  const actions = everything.filter((a) => a.module !== 'TEAM');
  const teamWaiting = actions.length !== everything.length;

  if (facts.screenplay.status === 'NO_ACCESS') {
    return {
      kind: 'UNAVAILABLE',
      module: null,
      title: 'Your role does not include the screenplay',
      description: 'Everything on this page starts from the screenplay. Ask the producer to share the script with your role.',
      ctas: [],
    };
  }

  if (facts.screenplay.status !== 'OK' || ctx.parse === 'UNKNOWN') {
    return {
      kind: 'UNAVAILABLE',
      module: null,
      title: 'The next step could not be worked out',
      description: 'Part of this production could not be read just now. Nothing is lost; try again in a moment.',
      ctas: [],
    };
  }

  if (ctx.parse === 'NONE') {
    const upload = actions.find((a) => a.id === 'screenplay.upload');
    return {
      kind: 'ACT',
      module: 'SCREENPLAY',
      title: 'Start with your screenplay',
      description: 'Upload your screenplay to extract its scenes and prepare your production workflows.',
      ctas: upload === undefined ? [] : [upload.cta],
    };
  }

  if (ctx.parse === 'PROCESSING' && screenplay !== null) {
    const queued = screenplay.run === null || screenplay.run.status === 'QUEUED' || screenplay.run.status === 'PENDING';
    return {
      kind: 'WAIT',
      module: 'SCREENPLAY',
      title: 'Your screenplay is being processed',
      description: queued
        ? `Draft v${String(screenplay.versionNumber)} is queued to be read. Its scenes appear when reading finishes.`
        : `Draft v${String(screenplay.versionNumber)} is being read into scenes. They appear when reading finishes.`,
      ctas: [ctx.cta('View progress', SCRIPT_TAB, null)],
    };
  }

  const blocking = actions.find((a) => a.category === 'BLOCKING');
  if (blocking !== undefined) return fromAction(blocking);

  const sceneStep = actions.find((a) => a.id === 'scenes.to-confirm' || a.id === 'scenes.finish-validation');
  if (sceneStep !== undefined) return fromAction(sceneStep);

  if (analysisRunning(ctx)) {
    const stage = screenplay?.run?.stages.find((s) => s.stage === 'EXTRACT');
    return {
      kind: 'WAIT',
      module: 'BREAKDOWN',
      title: 'The AI analysis is running',
      description:
        ctx.extraction === 'QUEUED'
          ? 'It is queued and starts shortly. Location requirements and production elements appear together when it finishes.'
          : `It is reading ${stage?.totalUnits ? sceneWord(stage.totalUnits) : 'the screenplay'} in one request. Location requirements and production elements appear together when it finishes.`,
      ctas: [ctx.cta('View progress', requirementsTarget(ctx), null)],
    };
  }

  const { locations: locWf, breakdown: bdWf } = facts.setup.workflows;

  if (ctx.workflowsOpen && L !== null && B !== null && bdWf === 'NOT_STARTED') {
    // AI RESULTS NOBODY HAS TOUCHED, in either workflow: review them, in either
    // order. One analysis wrote both, and neither review gates the other. Once a
    // person has decided anything, the action list's own order takes over.
    if (L.approved + L.dismissed === 0 && (L.undecided > 0 || B.toReview > 0)) {
      const ctas: OverviewCta[] = [];
      if (L.undecided > 0) {
        ctas.push(ctx.cta(`Review ${plural(L.undecided, 'location', 'locations')}`, requirementsTarget(ctx), 'REQUIREMENT_APPROVE'));
      }
      if (B.toReview > 0) {
        ctas.push(ctx.cta(`Review ${plural(B.toReview, 'element', 'elements')}`, elementsTarget(ctx), 'SCRIPT_REVIEW'));
      }
      const parts = [
        L.undecided > 0 ? plural(L.undecided, 'location requirement', 'location requirements') : null,
        B.toReview > 0 ? plural(B.toReview, 'production element', 'production elements') : null,
      ].filter((p): p is string => p !== null);

      return {
        kind: ctas.length > 1 ? 'CHOOSE' : 'ACT',
        module: L.undecided > 0 ? 'LOCATIONS' : 'BREAKDOWN',
        title: 'Review the AI analysis',
        description: `It suggested ${parts.join(' and ')}. Nothing is confirmed until a person says so, and the two reviews are independent: start with either.`,
        ctas,
      };
    }

    if (locWf === 'NOT_STARTED' && L.total === 0 && !hasBreakdownWork(B) && !ctx.extractionRan) {
      return {
        kind: 'CHOOSE',
        module: null,
        title: 'Choose your next workflow',
        description: `Your ${sceneWord(scenes?.active ?? 0)} are ready. Find filming locations or create the detailed breakdown — in either order; neither waits for the other.`,
        ctas: [
          ctx.cta('Find filming locations', { kind: 'FLOW', step: 'breakdown' }, 'REQUIREMENT_APPROVE'),
          ctx.cta('Create detailed breakdown', { kind: 'FLOW', step: 'elements' }, 'SCRIPT_REVIEW'),
        ],
      };
    }
  }

  const review = actions.find((a) => a.category === 'REVIEW');
  if (review !== undefined) return fromAction(review);

  const recommended = actions.find((a) => a.category === 'RECOMMENDED');
  if (recommended !== undefined) return fromAction(recommended);

  return {
    kind: 'UP_TO_DATE',
    module: null,
    title: 'Your current work is up to date',
    description: teamWaiting
      ? 'Nothing in the screenplay, scenes or workflows is waiting on a decision. That is not the same as a finished production — open a workflow to take it further.'
      : 'Nothing is waiting on a decision. That is not the same as a finished production — open a workflow to take it further.',
    ctas: ctx.workflowsOpen
      ? [ctx.cta('Open locations', LOCATIONS_TAB, null), ctx.cta('Open the breakdown', elementsTarget(ctx), null)]
      : [],
  };
}

// ─── The workflow map ──────────────────────────────────────────────────────────

function unavailableNode(id: OverviewNodeId, status: 'NO_ACCESS' | 'ERROR'): OverviewNode {
  return {
    id,
    state: 'UNAVAILABLE',
    headline: status === 'NO_ACCESS' ? 'Not shared with your role' : 'Could not be loaded',
    detail: status === 'NO_ACCESS' ? 'Your role on this production does not include this section.' : 'Try again in a moment.',
    target: null,
    attention: 0,
    steps: [],
  };
}

function screenplayNode(ctx: Ctx): OverviewNode {
  const section = ctx.facts.screenplay;
  if (section.status !== 'OK') return unavailableNode('SCREENPLAY', section.status);

  const s = ctx.screenplay;
  const base = { id: 'SCREENPLAY' as const, steps: [] };

  if (s === null) {
    return { ...base, state: 'NOT_STARTED', headline: 'No screenplay yet', detail: 'Upload or paste one to begin.', target: uploadTarget(ctx), attention: 0 };
  }

  const version = `Draft v${String(s.versionNumber)}`;
  const meta = [s.pageCount === null ? null : plural(s.pageCount, 'page', 'pages'), screenplayFormatLabel(s.sourceFormat)]
    .filter((p): p is string => p !== null)
    .join(' · ');

  if (ctx.parse === 'PROCESSING') {
    return { ...base, state: 'PROCESSING', headline: version, detail: 'Being read into scenes', target: SCRIPT_TAB, attention: 0 };
  }

  if (ctx.parse === 'FAILED') {
    return { ...base, state: 'FAILED', headline: version, detail: 'Could not be read', target: uploadTarget(ctx), attention: 1 };
  }

  if (ctx.parse === 'UNKNOWN') {
    return { ...base, state: 'UNAVAILABLE', headline: version, detail: 'Its scenes could not be loaded', target: SCRIPT_TAB, attention: 0 };
  }

  return {
    ...base,
    state: 'COMPLETED',
    headline: version,
    detail: s.versionCount > 1 ? `${meta} · ${plural(s.versionCount, 'draft', 'drafts')}` : meta,
    target: SCRIPT_TAB,
    attention: 0,
  };
}

function scenesNode(ctx: Ctx): OverviewNode {
  const base = { id: 'SCENES' as const };

  if (ctx.parse === 'UNKNOWN') return unavailableNode('SCENES', ctx.facts.scenes.status === 'NO_ACCESS' ? 'NO_ACCESS' : 'ERROR');

  if (ctx.parse !== 'READ') {
    return {
      ...base,
      state: 'NOT_STARTED',
      headline: 'Not started',
      detail: ctx.parse === 'PROCESSING' ? 'Waiting for the screenplay to be read' : 'Available once a screenplay is read',
      target: null,
      attention: 0,
      steps: [],
    };
  }

  if (ctx.facts.scenes.status !== 'OK') return unavailableNode('SCENES', ctx.facts.scenes.status);

  const s = ctx.scenes;
  if (s === null) return unavailableNode('SCENES', 'ERROR');

  const steps: OverviewStep[] = [
    {
      id: 'detected',
      label: 'Detected',
      value: String(s.active),
      state: s.active > 0 ? 'COMPLETED' : 'NEEDS_ATTENTION',
    },
    {
      id: 'confirmed',
      label: 'Confirmed',
      value: `${String(s.confirmed)} of ${String(s.active)}`,
      state: s.active > 0 && s.confirmed === s.active ? 'COMPLETED' : s.confirmed > 0 ? 'IN_PROGRESS' : 'READY_FOR_REVIEW',
    },
  ];

  const state: OverviewDisplayState =
    s.active === 0 || s.needsAttention > 0
      ? 'NEEDS_ATTENTION'
      : s.confirmed === s.active
        ? 'COMPLETED'
        : s.confirmed === 0
          ? 'READY_FOR_REVIEW'
          : 'IN_PROGRESS';

  const detail =
    s.needsAttention > 0
      ? `${String(s.needsAttention)} need attention`
      : s.toReview > 0
        ? `${String(s.toReview)} to review`
        : s.excluded > 0
          ? `${String(s.excluded)} excluded`
          : 'All confirmed';

  return {
    ...base,
    state,
    headline: state === 'COMPLETED' ? `${sceneWord(s.active)} confirmed` : `${String(s.active)} extracted`,
    detail,
    target: scenesTarget(s.needsAttention > 0 ? 'NEEDS_ATTENTION' : s.toReview > 0 ? 'TO_REVIEW' : undefined),
    attention: s.needsAttention + s.toReview,
    steps,
  };
}

function lockedNode(id: OverviewNodeId, ctx: Ctx): OverviewNode {
  return {
    id,
    state: 'NOT_STARTED',
    headline: 'Not started',
    detail: ctx.parse === 'READ' ? 'Available after scene validation' : 'Available once scenes are extracted',
    target: null,
    attention: 0,
    steps: [],
  };
}

function locationsNode(ctx: Ctx): OverviewNode {
  const section = ctx.facts.locations;
  if (section.status !== 'OK') return unavailableNode('LOCATIONS', section.status);

  const L = section.value;
  if (!ctx.workflowsOpen && L.total === 0) return lockedNode('LOCATIONS', ctx);

  // NOTHING TO COUNT YET: no analysis was asked for and nobody added one, so
  // "0 identified, 0 of 0 searched" would be zeros nobody measured.
  const steps: OverviewStep[] = L.total === 0 && !ctx.extractionRan ? [] : [
    {
      id: 'identified',
      label: 'Identified',
      value: String(L.total),
      state: L.total > 0 ? 'COMPLETED' : 'NOT_STARTED',
    },
    {
      id: 'reviewed',
      label: 'Reviewed',
      value: `${String(L.total - L.undecided)} of ${String(L.total)}`,
      state: L.total === 0 ? 'NOT_STARTED' : L.undecided === 0 ? 'COMPLETED' : 'READY_FOR_REVIEW',
    },
    {
      id: 'searched',
      label: 'Searched',
      value: `${String(L.searched)} of ${String(L.approved)}`,
      state: L.approved === 0 ? 'NOT_STARTED' : L.searched >= L.approved ? 'COMPLETED' : L.searched > 0 ? 'IN_PROGRESS' : 'NOT_STARTED',
    },
    {
      id: 'shortlisted',
      label: 'Shortlisted',
      value: `${String(L.shortlisted)} of ${String(L.approved)}`,
      state: L.approved === 0 || L.shortlisted === 0 ? 'NOT_STARTED' : L.shortlisted >= L.approved ? 'COMPLETED' : 'IN_PROGRESS',
    },
    {
      id: 'selected',
      label: 'Confirmed',
      value: `${String(L.selected)} of ${String(L.approved)}`,
      state: L.approved === 0 || L.selected === 0 ? 'NOT_STARTED' : L.selected >= L.approved ? 'COMPLETED' : 'IN_PROGRESS',
    },
  ];

  const target = L.undecided > 0 || L.total === 0 ? requirementsTarget(ctx) : L.approved > L.searched ? exploreTarget(ctx, null) : LOCATIONS_TAB;
  const outdated =
    L.needsRecheck > 0 ||
    (ctx.deps !== null && (ctx.deps.locationProfilesReopened > 0 || ctx.deps.openImpacts.blocker > 0 || ctx.deps.requirementsFromEarlierDraft > 0));
  const node = (state: OverviewDisplayState, headline: string, detail: string | null): OverviewNode => ({
    id: 'LOCATIONS',
    state,
    headline,
    detail,
    target,
    attention: L.undecided + L.needsRecheck,
    steps,
  });

  if (L.total === 0) {
    if (analysisRunning(ctx)) return node('PROCESSING', 'AI analysis running', 'Requirements appear when it finishes');
    if (ctx.extraction === 'FAILED') return node('FAILED', 'Analysis failed', 'Run it again');
    // NOT "0 locations": no analysis has been asked for. A claim about the
    // screenplay would need one.
    if (!ctx.extractionRan) return node('NOT_STARTED', 'Location analysis not started', null);

    return node('NEEDS_ATTENTION', 'No requirements suggested', 'Add the locations the screenplay needs by hand');
  }

  if (analysisRunning(ctx)) return node('PROCESSING', plural(L.total, 'requirement', 'requirements'), 'The AI analysis is running');
  if (outdated) return node('OUTDATED', plural(L.total, 'requirement', 'requirements'), 'Affected by a script change');
  if (L.approved === 0 && L.undecided > 0) return node('READY_FOR_REVIEW', `${String(L.undecided)} awaiting decision`, 'Approve or mark not needed');
  if (ctx.facts.setup.workflows.locations === 'COMPLETED') {
    return node('COMPLETED', `${String(L.approved)} approved`, `${String(L.selected)} of ${String(L.approved)} confirmed`);
  }

  return node(
    'IN_PROGRESS',
    `${String(L.approved)} approved`,
    L.undecided > 0 ? `${String(L.undecided)} still to review` : `${String(L.searched)} of ${String(L.approved)} searched`,
  );
}

function breakdownNode(ctx: Ctx): OverviewNode {
  const section = ctx.facts.breakdown;
  if (section.status !== 'OK') return unavailableNode('BREAKDOWN', section.status);

  const B = section.value;
  const has = hasBreakdownWork(B);
  if (!ctx.workflowsOpen && !has) return lockedNode('BREAKDOWN', ctx);

  const active = ctx.scenes?.active ?? 0;
  const analysed = ctx.scenes?.analysed ?? 0;
  const outOfDate = ctx.scenes?.outOfDate ?? 0;

  const steps: OverviewStep[] = !has && !ctx.extractionRan ? [] : [
    {
      id: 'read',
      label: 'AI reading',
      value: `${String(analysed)} of ${String(active)} scenes`,
      state: analysisRunning(ctx)
        ? 'PROCESSING'
        : outOfDate > 0
          ? 'OUTDATED'
          : analysed === 0
            ? 'NOT_STARTED'
            : analysed >= active
              ? 'COMPLETED'
              : 'IN_PROGRESS',
    },
    {
      id: 'elements',
      label: 'Element review',
      value: `${String(B.confirmedTags)} confirmed · ${String(B.toReview)} to review`,
      state: B.toReview > 0 ? 'READY_FOR_REVIEW' : B.confirmedTags > 0 ? 'COMPLETED' : 'NOT_STARTED',
    },
    {
      id: 'signed-off',
      label: 'Scenes signed off',
      value: `${String(B.scenesSignedOff)} of ${String(active)}`,
      state:
        B.scenesReopened > 0
          ? 'OUTDATED'
          : active > 0 && B.scenesSignedOff >= active
            ? 'COMPLETED'
            : B.scenesSignedOff > 0
              ? 'IN_PROGRESS'
              : 'NOT_STARTED',
    },
  ];

  const node = (state: OverviewDisplayState, headline: string, detail: string | null): OverviewNode => ({
    id: 'BREAKDOWN',
    state,
    headline,
    detail,
    target: elementsTarget(ctx),
    attention: B.toReview + B.scenesReopened,
    steps,
  });

  if (!has) {
    if (analysisRunning(ctx)) return node('PROCESSING', 'AI analysis running', 'Elements appear when it finishes');
    if (ctx.extraction === 'FAILED') return node('FAILED', 'Analysis failed', 'Run it again');
    if (!ctx.extractionRan) return node('NOT_STARTED', 'Detailed breakdown not started', null);

    return node('NEEDS_ATTENTION', 'No elements suggested', 'Add cast, props and the rest by hand');
  }

  const tags = plural(B.confirmedTags, 'element confirmed', 'elements confirmed');

  if (analysisRunning(ctx)) return node('PROCESSING', tags, 'The AI analysis is running');
  if (outOfDate > 0 || B.scenesReopened > 0) return node('OUTDATED', tags, 'Affected by a scene change');
  if (ctx.facts.setup.workflows.breakdown === 'COMPLETED') return node('COMPLETED', tags, 'Every scene signed off');
  if (B.confirmedTags === 0 && B.scenesSignedOff === 0 && B.toReview > 0) {
    return node('READY_FOR_REVIEW', `${String(B.toReview)} to review`, 'Suggested by the AI analysis');
  }

  return node('IN_PROGRESS', tags, B.toReview > 0 ? `${String(B.toReview)} still to review` : `${String(B.scenesSignedOff)} of ${String(active)} scenes signed off`);
}

// ─── Recent activity ─────────────────────────────────────────────────────────

/**
 * Where an activity line comes from. Every one is a record the production
 * already keeps — the decision log, the drafts, the pipeline runs, the
 * requirement events, the element decisions, the scene confirmations — read for
 * the Overview, never a second event store written for it.
 */
export type OverviewActivitySource =
  | 'PROJECT'
  | 'SCRIPT_UPLOADED'
  | 'PARSE'
  | 'ANALYSIS'
  | 'REQUIREMENT'
  | 'ELEMENT_DECISION'
  | 'SCENE_CONFIRMED'
  | 'SCENE_SIGNED_OFF';

export interface OverviewActivityItem {
  readonly id: string;
  readonly source: OverviewActivitySource;
  /** The record's own verb: a decision-log action, a requirement event, an element decision, a run status. */
  readonly action: string;
  /** Null for the system: a pipeline run records no person. */
  readonly actorUserId: number | null;
  readonly at: string;
  /** How many records this line stands for: consecutive ones, same person, same verb. */
  readonly count: number;
  /** The draft an upload or a run was about. */
  readonly versionNumber: number | null;
  readonly target: OverviewTarget | null;
}

/** Records further apart than this are two lines, however alike. */
export const OVERVIEW_ACTIVITY_GROUP_WINDOW_MS = 30 * 60 * 1000;

const GROUPABLE: ReadonlySet<OverviewActivitySource> = new Set([
  'REQUIREMENT',
  'ELEMENT_DECISION',
  'SCENE_CONFIRMED',
  'SCENE_SIGNED_OFF',
]);

/**
 * Newest first, with runs of the same act folded into one line: approving
 * thirty requirements in a sitting is "approved 30 location requirements", not
 * thirty lines pushing everything else off the list. Only consecutive records
 * fold, so the order of what happened is never rewritten.
 */
export function foldActivity(
  items: readonly OverviewActivityItem[],
  limit: number,
): OverviewActivityItem[] {
  const sorted = [...items].sort((a, b) => b.at.localeCompare(a.at) || a.id.localeCompare(b.id));
  const folded: { item: OverviewActivityItem; oldest: number }[] = [];

  for (const item of sorted) {
    const last = folded[folded.length - 1];
    const at = Date.parse(item.at);

    if (
      last !== undefined &&
      GROUPABLE.has(item.source) &&
      last.item.source === item.source &&
      last.item.action === item.action &&
      last.item.actorUserId === item.actorUserId &&
      last.oldest - at <= OVERVIEW_ACTIVITY_GROUP_WINDOW_MS
    ) {
      last.item = { ...last.item, count: last.item.count + item.count };
      last.oldest = at;
      continue;
    }

    if (folded.length === limit) break;
    folded.push({ item, oldest: at });
  }

  return folded.map((f) => f.item);
}
